import { IUser } from '../../slices/users/user';
import { UserTypes, UserTypeMap, UserTypesWeights, UserTypeMapForRepDataAccount } from '../../components/common/domain/userTypes';
import { AccessTypeOption } from '../../components/users/modal/accessType';
import { UserRoles } from '../../components/common/domain/userRoles';
import { SX_ADMIN_ACCOUNTS } from '../../components/users/modal/constants';
import { Features, isFeatureEnabled } from '../../featureToggles/features';
import { isExternalClient, isIncrowdClient } from '../clients';
import { ADMIN_ENABLED_ACCOUNTS_IDS, SX_ADMIN_ENABLED_ACCOUNTS_IDS } from '../../config';
import { isRepDataAccount } from '../utils';

const ADMIN_TYPES: ReadonlyArray<UserTypes> = [UserTypes.MainAdmin, UserTypes.SxAdmin];

export const isPlatformAdmin = (userType?: UserTypes): boolean => {
  if (!userType) {
    return false;
  }
  return ADMIN_TYPES.includes(userType);
};

export const isPlatformAdminAssistant = (userType?: UserTypes): boolean => {
  return userType === UserTypes.AdminAssistant;
};

export const isSxAdmin = (userType?: UserTypes): boolean => {
  return userType === UserTypes.SxAdmin;
};

export const isAccountAdmin = (userType?: UserTypes): boolean => {
  return userType === UserTypes.AccountAdmin;
};

interface IAvailableUserProps {
  loggedUser: IUser | null;
  /** @deprecated */
  accountName?: string;
  editingUser?: IUser;
  isTrialAccount?: boolean;
  accountId?: string;
}

export const getAvailableUserTypesWithNoFilter = (props: IAvailableUserProps): AccessTypeOption[] => {
  const { loggedUser, editingUser, isTrialAccount } = props;
  const loggedUserType = loggedUser?.userType || UserTypes.User;
  const loggedUserRoles = loggedUser?.roles || [];
  const editingUserType = editingUser?.userType || '';

  const options: AccessTypeOption[] = [];
  if (isTrialAccount) {
    UserTypeMap.forEach((value, key) => {
      options.push({
        label: value,
        value: key,
        key,
        disabled: key !== UserTypes.User
      });
    });
  } else if (editingUserType === UserTypes.SxAdmin) {
    UserTypeMap.forEach((value, key) => {
      options.push({
        label: value,
        value: key,
        key,
        disabled: true
      });
    });
  } else if (loggedUserType === UserTypes.SxAdmin) {
    UserTypeMap.forEach((value, key) => {
      options.push({
        label: value,
        value: key,
        key,
        disabled: key === UserTypes.SxAdmin && !loggedUserRoles.some(role => role === UserRoles.Support)
      });
    });
  } else {
    UserTypeMap.forEach((value, key) => {
      options.push({
        label: value,
        value: key,
        key,
        disabled: UserTypesWeights[loggedUserType] > UserTypesWeights[key] || loggedUserType === UserTypes.User
      });
    });
  }

  return options;

};

export const getAvailableUserTypes = (props: IAvailableUserProps): AccessTypeOption[] => {
  if (isFeatureEnabled(Features.PlgExternal) && !isIncrowdClient()) {
    return getUserTypeOptionsForPlgExternal(props);
  }
  const { loggedUser, accountName = '' } = props;
  const loggedUserType = loggedUser?.userType || UserTypes.User;
  const options = getAvailableUserTypesWithNoFilter(props);
  return options.filter(({ key }) => {
    return key !== UserTypes.SxAdmin || (loggedUserType === UserTypes.SxAdmin && SX_ADMIN_ACCOUNTS.includes(accountName));
  });
};

export const getUserTypeOptionsForPlgExternal = (props: IAvailableUserProps): AccessTypeOption[] => {
  const { loggedUser, editingUser, isTrialAccount, accountId = '' } = props;
  const loggedUserType = loggedUser?.userType || UserTypes.User;
  const loggedUserRoles = loggedUser?.roles || [];
  const editingUserType = editingUser?.userType || '';

  const showSxAdminType = loggedUserType === UserTypes.SxAdmin && SX_ADMIN_ENABLED_ACCOUNTS_IDS.has(accountId);
  const showAdminsType = isPlatformAdmin(loggedUserType) && ADMIN_ENABLED_ACCOUNTS_IDS.has(accountId);

  let userTypes = UserTypeMap;

  if (isRepDataAccount(accountId)) {
    userTypes = UserTypeMapForRepDataAccount;
  }

  let userTypeOptions = [...userTypes.entries()];
  if (!showSxAdminType) {
    userTypeOptions = userTypeOptions.filter(([key]) => key !== UserTypes.SxAdmin);
  }
  if (!showAdminsType || (!isExternalClient() && !isFeatureEnabled(Features.MainAdminAndAssistantForAll))) {
    userTypeOptions = userTypeOptions.filter(([key]) => key !== UserTypes.MainAdmin && key !== UserTypes.AdminAssistant);
  }

  if (isTrialAccount) {
    return userTypeOptions.map<AccessTypeOption>(([key, value]) => ({
      label: value,
      value: key,
      key,
      disabled: key !== UserTypes.User
    }));
  }
  if (editingUserType === UserTypes.SxAdmin) {
    return userTypeOptions.map<AccessTypeOption>(([key, value]) => ({
      label: value,
      value: key,
      key,
      disabled: true
    }));
  }
  if (loggedUserType === UserTypes.SxAdmin) {
    return userTypeOptions.map<AccessTypeOption>(([key, value]) => ({
      label: value,
      value: key,
      key,
      disabled: key === UserTypes.SxAdmin && !loggedUserRoles.some(role => role === UserRoles.Support)
    }));
  }

  return userTypeOptions.map<AccessTypeOption>(([key, value]) => ({
    label: value,
    value: key,
    key,
    disabled: UserTypesWeights[loggedUserType] > UserTypesWeights[key] || loggedUserType === UserTypes.User
  }));
};
